import { redirect } from "react-router-dom";
import { setTokenProvider } from "../utils/auth";
import {
  callAuthExchange,
  callPatchCurrentTenant,
  callWhoami,
} from "../utils/auth-api";
import { parseHashParams } from "../utils/url";
import type { WhoamiMe } from "../../shared-domain/auth-service/contract";

export interface AuthLoaderConfig {
  /** API エンドポイント */
  apiEndpoint: string;
  /** 認証ポータル URL */
  authPortalUrl: string;
  /** プロダクト ID */
  productId: string;
  /** トークンストア名（sessionStorage のキープレフィックス） */
  tokenStoreName: string;
  /** Cognito User Pool ID */
  userPoolId: string;
  /** Cognito Client ID */
  clientId: string;
  /** リフレッシュトークンAPIのURL */
  authRefreshUrl: string;
}

/**
 * 認証 loader を作成するヘルパー関数
 * @param config 認証設定
 * @returns loader 関数
 */
export function createAuthLoader(config: AuthLoaderConfig) {
  return async ({ request }: { request: Request }): Promise<WhoamiMe> => {
    const {
      apiEndpoint,
      authPortalUrl,
      productId,
      tokenStoreName,
      userPoolId,
      clientId,
      authRefreshUrl,
    } = config;

    // 認証ポータルの URL
    const params = new URLSearchParams();
    if (productId) {
      params.append("productId", productId);
    }
    const reAuthUrl = `${authPortalUrl}?${params.toString()}`;

    // Amplify の TokenProvider を初期化
    setTokenProvider(tokenStoreName, userPoolId, clientId, authRefreshUrl);

    // URL hash をパース
    const url = new URL(request.url);
    // const hash = parseHashParams(url.hash);
    const hashString = window.location.hash;
    const hash = parseHashParams(hashString);

    // authCode がある場合は hash を削除（履歴に残さない）
    if (hash.authCode) {
      const cleanUrl = `${url.pathname}${url.search}`;
      window.history.replaceState(null, "", cleanUrl);
    }

    try {
      // authCode がある場合はトークン交換
      let exchangeTenantId = null;
      if (hash.authCode) {
        const exchangeResponse = await callAuthExchange(
          hash.authCode,
          apiEndpoint,
          tokenStoreName,
        );

        if (!exchangeResponse.success) {
          console.error("Token exchange failed");
          throw redirect(reAuthUrl);
        }
        // ログイン画面で指定したテナントを初期表示する
        exchangeTenantId = exchangeResponse.body.tenantId;
      }

      // whoami で認証確認
      const whoamiResponse = await callWhoami(apiEndpoint);

      if (!whoamiResponse.success) {
        console.error("Authentication failed");
        // 自動ログインの無限リダイレクト防止
        if (hash.auto != null) {
          throw redirect(`${reAuthUrl}&logout`);
        }
        throw redirect(reAuthUrl);
      }

      // ログイン画面で指定したテナント (exchangeTenantId) がなければ
      // 最近表示したテナント (prevTenantId) を初期表示する
      const prevTenantId = whoamiResponse.me.currentTenantId;
      const tenantId = exchangeTenantId || prevTenantId;

      // テナントIDが取得できない場合は再ログインを促す
      if (!tenantId) {
        throw redirect(`${reAuthUrl}&logout`);
      }

      // ログイン時（authCode交換あり）は常にlastLoginDateを更新する
      // テナント変更がない場合でもPATCHを呼ぶことでproductsInfo.lastLoginDateが更新される
      // ページリフレッシュ時（authCodeなし）はAPI呼び出しを省略する
      if (tenantId !== prevTenantId || exchangeTenantId) {
        await callPatchCurrentTenant(apiEndpoint, tenantId);
      }

      return {
        ...whoamiResponse.me,
        currentTenantId: tenantId,
      };
    } catch (error) {
      // redirect() による throw は再スロー
      if (error instanceof Response) {
        throw error;
      }

      // その他のエラーは認証ポータルへリダイレクト
      console.error("Auth loader error:", error);
      if (hash.auto !== null) {
        throw redirect(`${reAuthUrl}&logout`);
      }
      throw redirect(reAuthUrl);
    }
  };
}
